import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
    uiStateAtomPageViewCount,
    PAGE_VIEWS_BEFORE_LOGIN_PROMPT,
    LOGIN_PROMPT_DELAY_MS,
    isCategoryPage,
} from '~/modules-rocco/root/states/ui-state-page-view-count';
import {
    uiStateAtomLoginModalOpened,
    uiStateAtomLoginModalOpenedByPrompt,
} from '~/modules-rocco/root/states/ui-state-login-modal-opened';
import { uiStateAtomAddProfessionalModalOpened } from '~/modules-rocco/root/states/ui-state-add-professional-modal-opened';
import { uiStateAtomLoginPromptSheetOpened } from '~/modules-rocco/root/states/ui-state-login-prompt-sheet-opened';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { useAuth } from '~/modules/root/context/AuthContext';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { useTracker } from '@archipro-website/tracker';
import { useFeature } from '~/modules/root';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useContentGateActive } from '~/modules-rocco/content-gate/use-content-gate-active';

export const LOGIN_PROMPT_TITLE =
    'Setup your free account to save ideas, get quotes, and connect with professionals';

/**
 * Prompts logged-out users to sign in every N page navigations.
 *
 * Rules:
 * - A 10-second delay is enforced after every navigation before the prompt can appear.
 * - On category pages the user must also have started scrolling.
 * - The prompt is suppressed while another modal is already open, and a
 *   queued prompt is cancelled (not postponed) when one opens first — the
 *   user just dealt with an auth surface; re-prompting the instant they
 *   close it would stack walls. The next threshold re-queues naturally.
 */
export const useLoginPromptOnPageView = () => {
    const { isLoggedIn } = useUserLoggedIn();
    const location = useLocation();
    const [pageViewCount, setPageViewCount] = useAtom(uiStateAtomPageViewCount);
    const { setLoginModal } = useSetLoginModal();
    const tracker = useTracker();

    const loginModalOpen = useAtomValue(uiStateAtomLoginModalOpened);
    // The AuthContext modal is a separate surface from the site-wide login
    // modal atom — enquiry, specifier, design-board and similar auth walls
    // open it, and the prompt must treat it exactly like the atom-driven one.
    const { authModalOpen } = useAuth();
    const setLoginModalOpenedByPrompt = useSetAtom(
        uiStateAtomLoginModalOpenedByPrompt,
    );
    const addProfessionalModalOpen = useAtomValue(
        uiStateAtomAddProfessionalModalOpened,
    );
    const [sheetOpen, setSheetOpen] = useAtom(
        uiStateAtomLoginPromptSheetOpened,
    );

    // Mobile-only sheet variant of the prompt (BC-4739).
    const { mobile: isMobile } = useAppDisplayModeContext();
    const sheetFlagOn = useFeature('login_prompt_sheet_2026');
    const useSheetVariant = isMobile && sheetFlagOn;

    // A gated category grid is already an auth wall on the page. Stacking the
    // site-wide prompt on top of it would ask the same visitor the same thing
    // twice in one breath.
    const contentGateActive = useContentGateActive();

    const previousPathname = useRef(location.pathname);

    const [promptPending, setPromptPending] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [navigationTimestamp, setNavigationTimestamp] = useState(Date.now());

    const isCategory = isCategoryPage(location.pathname);

    /**
     * Ref that always reflects whether any site-wide modal is open so
     * the setTimeout callback can read the latest value.
     */
    const isAnyModalOpenRef = useRef(false);
    useEffect(() => {
        isAnyModalOpenRef.current =
            loginModalOpen ||
            authModalOpen ||
            addProfessionalModalOpen ||
            sheetOpen;
    }, [loginModalOpen, authModalOpen, addProfessionalModalOpen, sheetOpen]);

    const openPrompt = useCallback(() => {
        setPromptPending(false);
        const variant = useSheetVariant ? 'sheet' : 'modal';
        // Source and ExtraData are the only custom keys the warehouse
        // pipeline persists (EXTRA_DATA_SOURCE / EVENT_EXTRA_DATA) — pack the
        // analytics into ExtraData as JSON; the flat keys serve network QA.
        tracker.log('LoginPromptShown', {
            url: new URL(window.location.href),
            data: {
                authSource: 'siteGate',
                variant,
                pageViewCount,
                Source: 'siteGate',
                ExtraData: JSON.stringify({
                    authSource: 'siteGate',
                    variant,
                    pageViewCount,
                }),
            },
        });
        if (useSheetVariant) {
            setSheetOpen(true);
            return;
        }
        setLoginModalOpenedByPrompt(true);
        setLoginModal({
            authSource: 'siteGate',
            isOpen: true,
            // No title: the modal takes its copy from the intent registry,
            // which is what the design specifies for a reader who arrived with
            // no stated intent. `LOGIN_PROMPT_TITLE` is the bottom sheet's own
            // copy and stays with the sheet above — passing it here overrode
            // the registry and put the sheet's line on the desktop modal.
            // The impression is reported above, with the variant and page-view
            // count this hook alone knows.
            skipPromptEvent: true,
        });
    }, [
        setLoginModal,
        tracker,
        pageViewCount,
        useSheetVariant,
        setSheetOpen,
        setLoginModalOpenedByPrompt,
    ]);

    // --- 1. Count page views and flag when threshold is reached -----------
    useEffect(() => {
        if (isLoggedIn) return;
        if (location.pathname === previousPathname.current) return;

        previousPathname.current = location.pathname;
        setNavigationTimestamp(Date.now());
        setHasScrolled(false);

        const newCount = pageViewCount + 1;
        setPageViewCount(newCount);

        // Don't queue another prompt while one is already showing — with the
        // non-blocking sheet the user can keep navigating, and a queued prompt
        // would reopen the instant they dismiss the current one.
        if (
            newCount % PAGE_VIEWS_BEFORE_LOGIN_PROMPT === 0 &&
            !isAnyModalOpenRef.current
        ) {
            setPromptPending(true);
        }
    }, [location.pathname, isLoggedIn, pageViewCount, setPageViewCount]);

    // --- 2. Cancel a queued prompt if any modal opens before it fires -----
    // Without this, the pending flag survives the other modal and the timer
    // re-arms with zero remaining delay on close — firing the prompt right
    // after the user declined a different auth wall.
    useEffect(() => {
        if (
            promptPending &&
            (loginModalOpen ||
                authModalOpen ||
                addProfessionalModalOpen ||
                sheetOpen)
        ) {
            setPromptPending(false);
        }
    }, [
        promptPending,
        loginModalOpen,
        authModalOpen,
        addProfessionalModalOpen,
        sheetOpen,
    ]);

    // --- 3. Track scrolling on category pages ----------------------------
    useEffect(() => {
        if (!isCategory || hasScrolled || isLoggedIn) return;

        const onScroll = () => setHasScrolled(true);
        window.addEventListener('scroll', onScroll, {
            once: true,
            passive: true,
        });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isCategory, hasScrolled, isLoggedIn, location.pathname]);

    // --- 4. Show the prompt after delay + conditions are met --------------
    useEffect(() => {
        if (!promptPending || isLoggedIn) return;
        if (contentGateActive) return;
        if (isCategory && !hasScrolled) return;
        if (
            loginModalOpen ||
            authModalOpen ||
            addProfessionalModalOpen ||
            sheetOpen
        ) {
            return;
        }

        const elapsed = Date.now() - navigationTimestamp;
        const remaining = Math.max(0, LOGIN_PROMPT_DELAY_MS - elapsed);

        const timer = setTimeout(() => {
            if (isAnyModalOpenRef.current) return;
            openPrompt();
        }, remaining);

        return () => clearTimeout(timer);
    }, [
        promptPending,
        isLoggedIn,
        contentGateActive,
        isCategory,
        hasScrolled,
        loginModalOpen,
        authModalOpen,
        addProfessionalModalOpen,
        sheetOpen,
        openPrompt,
        navigationTimestamp,
    ]);

    // --- 5. Close the sheet when another auth surface supersedes it -------
    // The sheet is non-blocking, so the user can open login from another
    // entry point (header, One Tap) or end up logged in while it is still
    // showing. Close it silently — this is not a user dismissal, so no
    // outcome event is logged and the showing lands in the no-outcome bucket.
    useEffect(() => {
        if (sheetOpen && (loginModalOpen || authModalOpen || isLoggedIn)) {
            setSheetOpen(false);
        }
    }, [sheetOpen, loginModalOpen, authModalOpen, isLoggedIn, setSheetOpen]);
};
